// Import GSAP for elegant animations
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import barba from "@barba/core";

// --------------------------------------------------
// 🐌 General
// --------------------------------------------------

// Register the ScrollToPlugin
gsap.registerPlugin(ScrollToPlugin);

gsap.registerPlugin(ScrollTrigger);

history.scrollRestoration = "manual";

gsap.defaults({
  ease: "power1.inOut",
  duration: 0.5,
});

// --------------------------------------------------
// 🔐 Magic Link Login
// --------------------------------------------------
const setupLogin = (container) => {
  document.addEventListener("DOMContentLoaded", function () {
    const loginForm = document.getElementById("magic-link-form");
    const submitButton = document.getElementById("login-submit");

    if (loginForm && submitButton) {
      // Store original button text
      const originalText = submitButton.textContent.trim();
      const sendingText =
        submitButton.getAttribute("data-sending-text") || "Sending...";

      loginForm.addEventListener("submit", function (e) {
        // Disable button to prevent double submission
        submitButton.disabled = true;
        submitButton.textContent = sendingText;

        // Re-enable button after 3 seconds (in case of error)
        setTimeout(() => {
          submitButton.disabled = false;
          submitButton.textContent = originalText;
        }, 3000);
      });
    }

    // Auto-hide error/success messages after 10 seconds
    const loginMessages = document.querySelectorAll(
      ".login-success, .login-error"
    );
    if (loginMessages.length > 0) {
      setTimeout(() => {
        loginMessages.forEach((message) => {
          message.style.opacity = "0.7";
        });
      }, 10000);
    }
  });
};

// --------------------------------------------------
// Images
// --------------------------------------------------
const setupLazyLoading = (container, callback) => {
  container.querySelectorAll(".lazy-image").forEach((img) => {
    if (img.complete) {
      img.classList.add("loaded");
    } else {
      img.addEventListener("load", () => img.classList.add("loaded"));
    }
  });
};
// --------------------------------------------------
// Responsive Videos
// --------------------------------------------------
const setupResponsiveVideos = (container) => {
  const responsiveVideos = container.querySelectorAll(".background-video");

  responsiveVideos.forEach((video) => {
    const desktopVideoUrl = video.dataset.desktopVideo;
    const tabletVideoUrl = video.dataset.tabletVideo;
    const mobileVideoUrl = video.dataset.mobileVideo;

    // Check if we have multiple video sources to work with
    if (!tabletVideoUrl && !mobileVideoUrl) {
      return; // No responsive videos needed
    }

    // Avoid stacking multiple listeners across Barba transitions
    if (video._responsiveCleanup) {
      video._responsiveCleanup();
    }

    let currentSource = "desktop";

    // Determine current source based on viewport width
    function getCurrentSource() {
      const width = window.innerWidth;
      if (width <= 540 && mobileVideoUrl) {
        return "mobile";
      } else if (width <= 1000 && tabletVideoUrl) {
        return "tablet";
      } else {
        return "desktop";
      }
    }

    // Get video URL for current source
    function getVideoUrlForSource(source) {
      switch (source) {
        case "mobile":
          return mobileVideoUrl || desktopVideoUrl;
        case "tablet":
          return tabletVideoUrl || desktopVideoUrl;
        default:
          return desktopVideoUrl;
      }
    }

    function updateVideoSource() {
      const newSource = getCurrentSource();

      if (newSource !== currentSource) {
        currentSource = newSource;

        // Update video source
        const newVideoUrl = getVideoUrlForSource(currentSource);
        if (newVideoUrl && newVideoUrl !== video.src) {
          // Create new source element
          const newSourceElement = document.createElement("source");
          newSourceElement.src = newVideoUrl;
          newSourceElement.type = "video/mp4";

          // Remove old sources and add new one
          const oldSources = video.querySelectorAll("source");
          oldSources.forEach((source) => source.remove());
          video.appendChild(newSourceElement);

          // Reload video to apply new source
          video.load();

          // Resume playback if it was playing
          if (!video.paused) {
            video.play().catch((e) => {
              // Ignore autoplay errors
            });
          }
        }
      }
    }

    // Listen for window resize
    window.addEventListener("resize", updateVideoSource);
    video._responsiveCleanup = () => {
      window.removeEventListener("resize", updateVideoSource);
    };

    // Initialize once metadata is available (or immediately if ready)
    if (video.readyState >= 1) {
      updateVideoSource();
    } else {
      video.addEventListener("loadedmetadata", updateVideoSource, {
        once: true,
      });
    }
  });
};

// --------------------------------------------------
// Background Video Scroll to Menu on Home Page
// --------------------------------------------------
const setupBackgroundVideoScrollToMenu = (container) => {
  const backgroundVideo = container.querySelector("#background-video");
  if (!backgroundVideo) {
    return;
  }

  // Check if we're on the home page (has fullscreen-video div)
  const fullscreenVideo = container.querySelector(".fullscreen-video");
  if (!fullscreenVideo) {
    return;
  }

  // Ensure video autoplays after Barba transitions
  if (backgroundVideo.paused) {
    backgroundVideo.play().catch((e) => {
      // If autoplay fails, try to play on first user interaction
      const playOnInteraction = () => {
        backgroundVideo.play().catch((e) => {});
        document.removeEventListener("click", playOnInteraction);
        document.removeEventListener("keydown", playOnInteraction);
        document.removeEventListener("touchstart", playOnInteraction);
      };
      document.addEventListener("click", playOnInteraction);
      document.addEventListener("keydown", playOnInteraction);
      document.addEventListener("touchstart", playOnInteraction);
    });
  }

  // Track if this is the first loop
  let isFirstLoop = true;
  let hasScrolledToMenu = false;
  let autoScrollPermanentlyCanceled = false;
  let lastTime = 0;

  // Single, clean GSAP scroll function
  function scrollToMenu() {
    // Check if auto-scroll has been permanently canceled
    if (autoScrollPermanentlyCanceled) {
      return;
    }

    // Check if user has already scrolled down more than 50% of viewport height
    const scrollThreshold = window.innerHeight * 0.5;
    if (window.scrollY > scrollThreshold) {
      autoScrollPermanentlyCanceled = true;
      return;
    }

    const menuElement = container.querySelector("#menu");
    if (menuElement && !hasScrolledToMenu) {
      hasScrolledToMenu = true;

      // Clean GSAP scroll with smooth easing
      gsap.to(window, {
        duration: 1.2,
        scrollTo: {
          y: menuElement,
          offsetY: 0,
        },
        ease: "power2.out",
      });
    }
  }

  // Separate function for manual button scroll (always works)
  function scrollToMenuManual(containerElement = container) {
    const menuElement = containerElement.querySelector("#menu");

    if (menuElement) {
      // Clean GSAP scroll with smooth easing
      gsap.to(window, {
        duration: 1.2,
        scrollTo: {
          y: menuElement,
          offsetY: 0,
        },
        ease: "power2.out",
      });
    }
  }

  // Listen for video time updates to detect first loop completion
  backgroundVideo.addEventListener("timeupdate", function () {
    // Only proceed if we have a valid duration and haven't scrolled yet
    if (
      backgroundVideo.duration &&
      !isNaN(backgroundVideo.duration) &&
      !hasScrolledToMenu
    ) {
      const currentTime = backgroundVideo.currentTime;

      // Check if video has looped back to beginning (time went backwards)
      if (currentTime < lastTime && lastTime > backgroundVideo.duration * 0.8) {
        scrollToMenu();
      }

      lastTime = currentTime;
    }
  });

  // Wait for video to be loaded before monitoring
  backgroundVideo.addEventListener("loadedmetadata", function () {
    lastTime = backgroundVideo.currentTime;
  });

  // Track user scrolling to provide real-time feedback
  let userHasScrolled = false;
  window.addEventListener("scroll", function () {
    const scrollThreshold = window.innerHeight * 0.5;
    if (window.scrollY > scrollThreshold && !userHasScrolled) {
      userHasScrolled = true;
      autoScrollPermanentlyCanceled = true; // Permanently cancel auto-scroll
    }
  });

  // Add click functionality to the scroll-down button
  const scrollButton = container.querySelector(".scroll-button");
  if (scrollButton) {
    scrollButton.addEventListener("click", function () {
      scrollToMenuManual(); // Use the new manual scroll function
    });
  }
};

// --------------------------------------------------
// Home Content ScrollTrigger for Language Color Change
// --------------------------------------------------
const setupHomeContentScrollTrigger = (container) => {
  const homeContent = container.querySelector(".home-content");
  const languages = container.querySelector(".languages");

  if (!homeContent || !languages) {
    return;
  }

  // Create ScrollTrigger to change language color when home-content reaches 100px from top
  ScrollTrigger.create({
    trigger: homeContent,
    start: "top 50px", // When top of home-content is 100px from top of viewport
    end: "bottom top", // Until bottom of home-content reaches top of viewport
    onEnter: () => {
      gsap.to(languages, {
        color: "var(--off-black)",
        duration: 0.3,
        ease: "power2.out",
      });
    },
    onLeave: () => {
      gsap.to(languages, {
        color: "var(--white)",
        duration: 0.3,
        ease: "power2.out",
      });
    },
    onEnterBack: () => {
      gsap.to(languages, {
        color: "var(--off-black)",
        duration: 0.3,
        ease: "power2.out",
      });
    },
    onLeaveBack: () => {
      gsap.to(languages, {
        color: "var(--white)",
        duration: 0.3,
        ease: "power2.out",
      });
    },
  });
};

// --------------------------------------------------
// ✨ Page transitions
// --------------------------------------------------

const updateHeader = (currentContainer, nextContainer) => {
  duration = 0;

  let oldState = [];
  if (currentContainer) {
    duration = 0.5;
    oldState = JSON.parse(currentContainer.dataset.state);
  }
  let newState = JSON.parse(nextContainer.dataset.state);

  // Menu

  if (newState.menu !== false) {
    gsap.to(".menu-button", {
      autoAlpha: 1,
      duration: duration,
    });
  } else {
    gsap.to(".menu-button", {
      autoAlpha: 0,
      duration: duration,
    });
  }

  // Language switcher dark

  if (newState.languageSwitcherDark === true) {
    gsap.to(".languages", {
      color: "var(--off-black)",
      duration: duration,
    });
  } else {
    gsap.to(".languages", {
      color: "var(--white)",
      duration: duration,
    });
  }
};

barba.hooks.beforeEnter((data) => {
  updateHeader(data.current.container, data.next.container, false);
});

barba.hooks.before((data) => {
  document.documentElement.classList.add("loading");
});
barba.hooks.leave((data) => {
  document.documentElement.classList.remove("loading");
});

barba.hooks.after((data) => {
  ScrollTrigger.refresh();
  ScrollToPlugin.refresh();
});

barba.init({
  // debug: true,
  views: [
    // Home view
    {
      namespace: "home",
      beforeEnter(data) {
        setupLazyLoading(data.next.container);
        setupBackgroundVideoScrollToMenu(data.next.container);
        setupResponsiveVideos(data.next.container);
        setupHomeContentScrollTrigger(data.next.container);
      },
    },
    // Video project view
    {
      namespace: "video-project",
      beforeEnter(data) {
        setupLazyLoading(data.next.container);
      },
      afterEnter(data) {},
    },
    // Login view
    {
      namespace: "login",
      beforeEnter(data) {
        setupLogin(data.next.container);
      },
    },
  ],
  transitions: [
    {
      sync: true, // Enable sync mode for concurrent transitions
      leave({ current }) {
        // Animate the current page to fade out
        return gsap.to(current.container, { opacity: 0, duration: 0.5 });
      },
      enter({ next }) {
        // Animate the next page to fade in
        return gsap.from(next.container, { opacity: 0, duration: 0.5 });
      },
    },
  ],
});
